import { Dispatch, SetStateAction, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import { css, styled } from 'styled-components';

import styles from './styles.module.css';
import { theme } from '../../styles';
import { Text } from '../Text';
import { Button } from '../Button';
import { FileIcon, TriangleExclamationIcon, UploadIcon } from '../../assets/icons';
import { Container, Flex } from '../Layout';
import { useMediaMin } from '../Media/useMediaMin';
import { useTabbing } from '../../utils';

const REMOVE_FOCUS_WITHIN_BORDER = styles['remove-focus-within-border'];

const StyledButton = styled(Button)`
  background-color: transparent;
  margin-top: 30px;
  &:focus {
    outline: ${theme.colors.foraBlue} solid 2px;
    outline-offset: 2px;
    background-color: ${theme.colors.foraBlue};
    color: ${theme.colors.white};
  }

  /* Don't show focus outline on button click (only when keyboard navigating) */
  &:focus:not(:focus-visible) {
    outline: 0;
  }
`;

/** Styling for main container */
const StyledContainer = styled(Container)<{ loading?: boolean; isDragging?: boolean; isTabbing?: boolean }>`
  background-color: ${props =>
    props.isDragging ? theme.colors.black20 : props.loading ? theme.colors.white : theme.colors.black05};
  &:hover {
    background-color: ${theme.colors.black10};
  }
  &:hover ${StyledButton} {
    background-color: ${theme.colors.foraBlue};
    color: ${theme.colors.white};

    &:hover {
      background-color: ${theme.colors.darkBlue};
    }
  }

  ${props =>
    props.isTabbing &&
    css`
      &:focus-within {
        outline: 3px solid ${theme.colors.foraBlue};
        outline-offset: 7px;

        ${StyledButton} {
          background-color: ${theme.colors.foraBlue};
          color: ${theme.colors.white};
        }
      }
    `}

  /** Styling for loading state stripes */
  ${props =>
    props.loading
      ? css`
          position: relative;
          &:before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-image: repeating-linear-gradient(
              -45deg,
              transparent,
              transparent 90px,
              ${theme.colors.black05} 90px,
              ${theme.colors.black05} 180px
            );
            background-size: 200% 100%;
            background-color: transparent;
            animation: loading-slide 3s linear infinite;
          }

          @keyframes loading-slide {
            from {
              background-position: right;
            }
            to {
              background-position: left;
            }
          }
        `
      : css`
          position: relative;
          &:before {
            content: none;
          }
        `}
`;

/** Styling for dashed border and content */
const StyledFlex = styled(Flex)`
  background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='4' ry='4' stroke='%23B2B2B2FF' stroke-width='4' stroke-dasharray='14%2c 8' stroke-dashoffset='0' stroke-linecap='butt'/%3e%3c/svg%3e");
  border-radius: 4px;
  justify-content: center;
  flex-direction: column;
  padding: 28px 0px 35px 0px;
  align-items: center;
  &:focus {
    outline: ${theme.colors.foraBlue} solid 4px;
  }
`;

type FileDropProps = {
  loading?: boolean;
  multiple?: boolean;
  text?: string;
  errorText?: string;
  types: string[];
  sendFile: (file: File | null) => void;
  fileName: string | null;
  setFileName?: Dispatch<SetStateAction<string | null>>;
};

const formatTypesText = (types: string[]): string => {
  if (types.length === 1) {
    return `Tillåtet filformat: ${types[0]}`;
  } else {
    const lastType = types[types.length - 1];
    const formattedTypes = types.slice(0, -1).join(', ');
    return `Tillåtna filformat: ${formattedTypes} och ${lastType}`;
  }
};

export const FileDrop = ({
  loading,
  multiple,
  types,
  text = 'Välj en fil eller dra den hit',
  sendFile,
  fileName,
  setFileName,
}: FileDropProps) => {
  const [fileError, setFileError] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const isDesktop = useMediaMin(theme.breakpoint.lg);

  const handleFilePicker = (file: File) => {
    if (file && setFileName) {
      setFileError(false);
      setFileName(file.name);
    }
    sendFile(file);
  };

  const handleError = (error: string) => {
    if (error) {
      setFileError(Boolean(error));
      if (setFileName) {
        setFileName(null);
      }
    } else {
      setFileError(false);
    }
  };

  const handleDraggingStateChange = (isDragging: boolean) => {
    setIsDragging(isDragging);
  };

  const handleOnDrop = () => {
    setIsDragging(false);
  };

  const truncateFileString = (fileName: string | null) => {
    const maxStringLength = isDesktop ? 40 : 30;
    const dots = '...';

    if (fileName && fileName.length <= maxStringLength) {
      return fileName;
    }

    if (fileName && fileName.length > maxStringLength) {
      const firstPortion = fileName.slice(0, maxStringLength / 2);
      const endPortion = fileName.slice(-firstPortion.length, fileName.length);

      return `${firstPortion}${dots}${endPortion}`;
    }
    return fileName;
  };
  const isTabbing = useTabbing();

  return (
    <StyledContainer
      loading={loading}
      isDragging={isDragging}
      isTabbing={isTabbing}
      maxWidth={['100%', 490]}
      maxHeight={[249, 249, 233]}
      marginBottom={40}
    >
      <FileDropContent
        handleFilePicker={handleFilePicker}
        handleDraggingStateChange={handleDraggingStateChange}
        handleError={handleError}
        handleOnDrop={handleOnDrop}
        types={types}
        multiple={multiple}
        fileName={truncateFileString(fileName)}
        fileError={fileError}
        text={text}
        loading={loading}
      />
    </StyledContainer>
  );
};

const FileDropContent = ({
  handleFilePicker,
  handleError,
  handleDraggingStateChange,
  handleOnDrop,
  types,
  multiple,
  fileName,
  fileError,
  loading,
  text,
}: {
  handleFilePicker: (file: File) => void;
  handleError: (error: string) => void;
  handleDraggingStateChange: (isDragging: boolean) => void;
  handleOnDrop: () => void;
  types: string[];
  multiple?: boolean;
  fileName: string | null;
  fileError: boolean;
  loading?: boolean;
  text: string;
}) => {
  return (
    <FileUploader
      handleChange={handleFilePicker}
      name="file"
      types={types}
      hoverTitle={<></>}
      multiple={multiple}
      onTypeError={handleError}
      onDrop={handleOnDrop}
      onDraggingStateChange={handleDraggingStateChange}
      classes={REMOVE_FOCUS_WITHIN_BORDER}
      dropMessageStyle={{
        border: 'none', // Remove hidden label border on drop (visual artifact)
      }}
    >
      <StyledFlex>
        {/* Icon */}
        {fileName ? (
          <FileIcon
            color="foraBlue"
            containerSize={30}
            width={30}
            data-testid="test-processing-in-progress-loading-element"
          />
        ) : fileError ? (
          <TriangleExclamationIcon containerSize={30} width={30} color={'amaranth'} />
        ) : (
          <UploadIcon containerSize={30} width={30} color={'foraBlue'} />
        )}
        {/* Title text */}
        <Text
          color={fileError ? 'amaranth' : fileName ? 'foraBlue' : 'black'}
          size={'medium'}
          renderAs="span"
          marginTop={10}
        >
          {loading ? 'Bearbetar...' : fileError ? 'Filen du valde är av fel filformat' : fileName ? fileName : text}
        </Text>
        {/* Allowed file formats text */}
        <Text color={'black70'} size="small" renderAs="span">
          {loading ? 'Vänta medan filen analyseras' : formatTypesText(types)}
        </Text>
        {/* Button */}
        <Flex>
          <StyledButton variant="outline" onClick={() => handleFilePicker}>
            {fileName ? 'Byt fil' : 'Välj fil'}
          </StyledButton>
        </Flex>
      </StyledFlex>
    </FileUploader>
  );
};
